import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/config/env';
import { tokenStorage } from '@vnetwork/margarita-core/auth';
import { decodeJwtPayload } from '@vnetwork/margarita-core/auth';
import { endKeycloakSession } from '@/lib/sso';
import { SESSION_ENDED_EVENT } from '@vnetwork/margarita-shell/session-ended';

/** Broadcast a forced logout so the auth layer can react (clear + redirect). */
export const AUTH_LOGOUT_EVENT = SESSION_ENDED_EVENT;
function forceLogout() {
  // End the Keycloak SSO session before clearing (AuthProvider's logout runs
  // after clear() and can't anymore). Matters for forced logouts that never
  // attempted a refresh (the v2 body-401 path below): the Keycloak cookie is
  // still alive there, and leaving it would re-lock the next login to the old
  // account. After a FAILED refresh the token is already dead — the POST just
  // 400s and is swallowed.
  const refresh = tokenStorage.refresh;
  if (refresh && tokenStorage.authMethod !== 'local') {
    endKeycloakSession(refresh);
  }
  tokenStorage.clear();
  window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
}

/** `exp` (absolute UNIX seconds) from a JWT payload, or null if unparsable. */
function jwtExp(token: string): number | null {
  const exp = Number(decodeJwtPayload(token).exp);
  return Number.isFinite(exp) ? exp : null;
}

/**
 * Local-auth refresh — `POST {AUTH_URL}/auth/v2/refresh_token`. The auth service
 * answers 200 with `data.data.{access_token, exp, refresh_token?}` (`exp` is an
 * ABSOLUTE UNIX timestamp in seconds) and signals failure via a 200 + `errors`
 * body. Mirrors legacy @console `refreshTokenLocal`.
 */
async function refreshLocal(refresh: string): Promise<string | null> {
  const res = await axios.post(`${env.authUrl}/auth/v2/refresh_token`, {
    refresh_token: refresh,
  });
  if (res.data?.errors) return null;
  const d = res.data?.data ?? {};
  if (!d.access_token || d.exp == null) return null;
  tokenStorage.set({
    access: d.access_token,
    // Keep the old refresh token unless the server rotated it.
    refresh: d.refresh_token ?? refresh,
    expSeconds: Number(d.exp),
  });
  return d.access_token as string;
}

/**
 * SSO refresh — Keycloak's standard `refresh_token` grant on the realm token
 * endpoint. Expiry comes from the new access token's own `exp` claim. Mirrors
 * legacy @console `refreshTokenSSO`.
 */
async function refreshSso(refresh: string): Promise<string | null> {
  const tokenUrl = `${env.keycloak.url}/realms/${env.keycloak.realm}/protocol/openid-connect/token`;
  const res = await axios.post(
    tokenUrl,
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.keycloak.clientId,
      refresh_token: refresh,
    }),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  const access = res.data?.access_token;
  if (!access) return null;
  tokenStorage.set({
    access,
    refresh: res.data.refresh_token ?? refresh,
    expSeconds: jwtExp(access) ?? undefined,
  });
  return access as string;
}

/**
 * Refresh the access token against the auth backend that issued it — local auth
 * service or Keycloak, per the stored AUTH_METHOD (same dispatch as the legacy
 * @console). Single-flight: concurrent callers share one in-flight refresh.
 * A failed refresh forces logout (clears tokens + redirects via the auth layer).
 */
let refreshInFlight: Promise<string | null> | null = null;
async function refreshAccessToken(): Promise<string | null> {
  const refresh = tokenStorage.refresh;
  if (!refresh) return null;
  if (refreshInFlight) return refreshInFlight;

  // Only classic local-auth sessions refresh against the local service; SSO and
  // standalone-Google sessions are both Keycloak-issued, so they refresh there.
  const doRefresh = tokenStorage.authMethod === 'local' ? refreshLocal : refreshSso;
  refreshInFlight = doRefresh(refresh)
    .catch(() => null)
    .then((access) => {
      if (!access) forceLogout();
      return access;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/** Attach a bearer token (refreshing first if expired) to a request config. */
async function withToken(
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> {
  let access = tokenStorage.access;
  if (access && tokenStorage.isExpired) {
    access = await refreshAccessToken();
  }
  if (access) {
    config.headers.set('Authorization', `Bearer ${access}`);
  }
  return config;
}

interface CreateClientOptions {
  /**
   * Extra request interceptors, registered right after the token one, in order.
   * Axios runs request interceptors last-registered-first, so these run BEFORE
   * the token is attached: exactly what `instance.interceptors.request.use(x)`
   * after `createClient()` did.
   */
  requestInterceptors?: Array<
    (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>
  >;
}

/** Factory: an authenticated axios instance bound to a base URL. */
export function createClient(baseURL: string, options: CreateClientOptions = {}): AxiosInstance {
  const instance = axios.create({ baseURL, timeout: 30_000 });

  instance.interceptors.request.use(withToken);
  for (const interceptor of options.requestInterceptors ?? []) {
    instance.interceptors.request.use(interceptor);
  }

  instance.interceptors.response.use(
    (res) => {
      // Legacy v2 quirk: auth expiry can come back as HTTP 200 with a 401/4005
      // body. Treat it as a dead session (same as legacy @console).
      if (res.data?.status === 401 || res.data?.errors?.code === 4005) {
        forceLogout();
        return Promise.reject(new Error('Authentication expired'));
      }
      return res;
    },
    async (error) => {
      const status = error?.response?.status;
      const original = error?.config as
        | (AxiosRequestConfig & { _retried?: boolean })
        | undefined;

      // One retry after a token refresh on 401.
      if (status === 401 && original && !original._retried) {
        original._retried = true;
        const access = await refreshAccessToken();
        if (access) {
          original.headers = {
            ...original.headers,
            Authorization: `Bearer ${access}`,
          };
          return instance.request(original);
        }
        forceLogout();
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

/**
 * Factory: an unauthenticated axios instance (no token, no 401→logout). Use for
 * the auth flow itself — login, verification, forgot/reset — where a 401 is a
 * normal "bad credentials" answer, not a session expiry.
 */
export function createPublicClient(baseURL: string): AxiosInstance {
  return axios.create({ baseURL, timeout: 30_000 });
}

